import { Box, Typography } from '@mui/material';
import React from 'react';
import { Components } from 'react-markdown';

import { Link } from '@sbu/components.link';
import { useTheme } from '@sumologic/yama-ds-components';

export const useMarkdownRenderers = (): Components => {
  const { palette } = useTheme();

  const markdownRenderers: Components = {
    // use custom renderer for link to get the right text color
    a: ({ href, ...props }) =>
      href ? (
        <Link
          href={href}
          rel='noopener noreferrer'
          sx={{ fontSize: '1rem', textDecoration: 'none' }}
          target='_blank'
          variant='a'
          {...props}
        />
      ) : null,
    // Headings with proper hierarchy
    h1: ({ children }: any) => (
      <Typography variant='h2' sx={{ mt: 3, mb: 1.5 }}>
        {children}
      </Typography>
    ),
    h2: ({ children }: any) => (
      <Typography variant='h3' sx={{ mt: 2.5, mb: 1 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }: any) => (
      <Typography variant='h4' sx={{ mt: 2, mb: 1 }}>
        {children}
      </Typography>
    ),
    // Paragraphs
    p: ({ children }: any) => (
      <Typography variant='body1' sx={{ mb: 0 }}>
        {children}
      </Typography>
    ),
    // Lists
    ul: ({ children }: any) => (
      <Box
        component='ul'
        sx={{
          pl: 3,
          my: 0,
          '& li': { my: 0.5 },
          whiteSpace: 'normal',
        }}
      >
        {children}
      </Box>
    ),
    ol: ({ children }: any) => (
      <Box
        component='ol'
        sx={{
          pl: 3,
          my: 0,
          '& li': { my: 0.5 },
          whiteSpace: 'normal',
        }}
      >
        {children}
      </Box>
    ),
    // Inline code
    code: ({ children }: { children: React.ReactNode }) => (
      <Box
        component='code'
        sx={{
          backgroundColor: palette.tokens.YamaInlineCodeBackgroundColor,
          color: palette.tokens.YamaInlineCodeColor,
          border: `${palette.tokens.YamaInlineCodeBorderWidth} ${palette.tokens.YamaInlineCodeBorderStyle}`,
          borderColor: palette.tokens.YamaInlineCodeBorderColor,
          paddingLeft: palette.tokens.YamaInlineCodePaddingLeft,
          paddingRight: palette.tokens.YamaInlineCodePaddingRight,
          paddingTop: palette.tokens.YamaInlineCodePaddingTop,
          paddingBottom: palette.tokens.YamaInlineCodePaddingBottom,
          borderRadius: palette.tokens.YamaInlineCodeBorderRadius,
          fontSize: palette.tokens.YamaInlineCodeFontSize,
          lineHeight: palette.tokens.YamaInlineCodeLineHeight,
          fontFamily: palette.tokens.YamaInlineCodeFontFamily,
        }}
      >
        {children}
      </Box>
    ),
  };

  return markdownRenderers;
};
